import { Routes } from '@angular/router';
import { provideState } from '@ngrx/store';

import { getQuickNavigationRoute } from '@core/config/quick-navigation-routes.config';
import { RouteSegment } from '@core/enums/route-segment.enum';
import { createFeatureGuard } from '@core/guards/feature-guard';
import { globalChargersEnabledCanActivateGuard } from '@core/guards/global-chargers-enabled-guard';
import { globalVehiclesEnabledCanActivateGuard } from '@core/guards/global-vehicles-enabled-guard';
import { optimoTenantCanActivateGuard } from '@core/guards/optimo-tenant-guard';
import { createPermissionGuard } from '@core/guards/permission-guard';
import { themeCanActivateGuard } from '@core/guards/theme-guard';

import { PermissionStoreModelEnum } from '@domain/permissions/permissions.enums';

import { KIOSK_EVSE_GRID_APPEARANCE } from './pages/kiosk/kiosk.types';
import { LayoutComponent } from './pages/layout';
import { layoutFeatureKey, layoutReducer } from './pages/layout/store/layout.reducer';
import { MultiSiteDashboardRoutes } from './pages/multi-site-dashboard/multi-site-dashboard.routes';

export const routes: Routes = [
  {
    path: `${RouteSegment.KioskNew}/:siteId`,
    canActivate: [
      themeCanActivateGuard,
      optimoTenantCanActivateGuard,
      createPermissionGuard([
        PermissionStoreModelEnum.ViewAnyKiosk,
        PermissionStoreModelEnum.ViewOwnKiosk,
        PermissionStoreModelEnum.ViewAnySites,
        PermissionStoreModelEnum.ViewOwnSites,
      ]),
    ],
    providers: [{ provide: KIOSK_EVSE_GRID_APPEARANCE, useValue: 'current' }],
    loadChildren: () => import('./pages/kiosk/kiosk.routes').then((m) => m.KioskRoutes),
  },
  {
    path: `${RouteSegment.Kiosk}/:siteId`,
    canActivate: [
      themeCanActivateGuard,
      createPermissionGuard([
        PermissionStoreModelEnum.ViewAnyKiosk,
        PermissionStoreModelEnum.ViewOwnKiosk,
        PermissionStoreModelEnum.ViewAnySites,
        PermissionStoreModelEnum.ViewOwnSites,
      ]),
    ],
    loadChildren: () => import('./pages/kiosk/kiosk.routes').then((m) => m.KioskRoutes),
  },
  {
    path: `${RouteSegment.MobileKiosk}/:siteId`,
    canActivate: [
      themeCanActivateGuard,
      createPermissionGuard([
        PermissionStoreModelEnum.ViewAnyKiosk,
        PermissionStoreModelEnum.ViewOwnKiosk,
        PermissionStoreModelEnum.ViewAnySites,
        PermissionStoreModelEnum.ViewOwnSites,
      ]),
    ],
    loadChildren: () => import('./pages/kiosk/mobile-kiosk.routes').then((m) => m.MobileKioskRoutes),
  },
  {
    path: `${RouteSegment.KioskTv}/:siteId`,
    canActivate: [
      themeCanActivateGuard,
      createPermissionGuard([
        PermissionStoreModelEnum.ViewAnyKiosk,
        PermissionStoreModelEnum.ViewOwnKiosk,
        PermissionStoreModelEnum.ViewAnySites,
        PermissionStoreModelEnum.ViewOwnSites,
      ]),
    ],
    loadChildren: () => import('./pages/kiosk/kiosk.routes').then((m) => m.KioskRoutes),
  },
  {
    path: RouteSegment.Root,
    component: LayoutComponent,
    providers: [
      provideState({
        name: layoutFeatureKey,
        reducer: layoutReducer,
      }),
    ],
    title: '',
    canActivate: [themeCanActivateGuard],
    children: [
      {
        path: RouteSegment.Root,
        pathMatch: 'full',
        children: MultiSiteDashboardRoutes,
      },
      // TODO - commented dashboard route
      /* {
        path: RouteSegment.Root,
        pathMatch: 'full',
        component: DashboardComponent,
        providers: [
          provideState({
            name: dashboardFeatureKey,
            reducer: dashboardReducer,
          }),
          provideEffects(DashboardEffects),
        ],
      }, */
      {
        path: RouteSegment.Sites,
        children: [
          {
            path: '',
            loadChildren: () => import('./pages/sites/sites.routes').then((m) => m.SitesRoutes),
          },
          {
            path: ':siteId',
            loadChildren: () => import('./pages/single-site/single-site.routes').then((m) => m.SingleSiteRoutes),
          },
        ],
        title: 'side-menu.sites',
      },
      {
        path: RouteSegment.IdTags,
        loadChildren: () => import('./pages/id-tags/id-tags.routes').then((m) => m.IdTagsRoutes),
        canActivate: [createPermissionGuard(getQuickNavigationRoute('id-tags').permissions!)],
        title: 'side-menu.id-tags',
      },
      {
        path: RouteSegment.Alerting,
        loadChildren: () => import('./pages/alerting/alerting.routes').then((m) => m.AlertingRoutes),
        title: 'side-menu.alerting',
      },
      {
        path: RouteSegment.Reports,
        loadChildren: () => import('./pages/reports/reports.routes').then((m) => m.ReportsRoutes),
        canActivate: [createPermissionGuard(getQuickNavigationRoute('reports').permissions!)],
        title: 'side-menu.reports',
      },
      {
        path: RouteSegment.Vehicles,
        loadChildren: () => import('./pages/vehicles/vehicles.routes').then((m) => m.VehiclesRoutes),
        canActivate: [globalVehiclesEnabledCanActivateGuard, createPermissionGuard(getQuickNavigationRoute('vehicles').permissions!)],
        title: 'side-menu.vehicles',
      },
      {
        path: RouteSegment.Chargers,
        loadChildren: () => import('./pages/chargers/chargers.routes').then((m) => m.ChargersRoutes),
        canActivate: [globalChargersEnabledCanActivateGuard, createPermissionGuard(getQuickNavigationRoute('chargers').permissions!)],
        title: 'global-chargers.chargers.title',
      },
      {
        path: RouteSegment.Sharing,
        loadChildren: () => import('./pages/sharing/sharing.routes').then((m) => m.SharingRoutes),
        canActivate: [
          createFeatureGuard(getQuickNavigationRoute('sharing').featureFlags!),
          createPermissionGuard(getQuickNavigationRoute('sharing').permissions!),
        ],
        title: 'side-menu.sharing',
      },
      {
        path: RouteSegment.Roaming,
        loadChildren: () => import('./pages/roaming/roaming.routes').then((m) => m.RoamingRoutes),
        canActivate: [
          createFeatureGuard(getQuickNavigationRoute('roaming').featureFlags!),
          createPermissionGuard(getQuickNavigationRoute('roaming').permissions!),
        ],
        title: 'side-menu.roaming',
      },
      {
        path: RouteSegment.Flexibility,
        loadChildren: () => import('./pages/flexibility/flexibility.routes').then((m) => m.FlexibilityRoutes),
        canActivate: [
          createFeatureGuard(getQuickNavigationRoute('flexibility').featureFlags!),
          createPermissionGuard(getQuickNavigationRoute('flexibility').permissions!),
        ],
        title: 'side-menu.flexibility',
      },
      {
        path: RouteSegment.EnergyTrading,
        loadChildren: () => import('./pages/energy-trading/energy-trading.routes').then((m) => m.EnergyTradingRoutes),
        canActivate: [createPermissionGuard(getQuickNavigationRoute('energy-trading').permissions!)],
        title: 'side-menu.energy-trading',
      },
      {
        path: RouteSegment.WorkflowManagement,
        loadChildren: () => import('./pages/workflow-management/workflow-management.routes').then((m) => m.WorkflowManagementRoutes),
        canActivate: [createPermissionGuard(getQuickNavigationRoute('workflow').permissions!)],
        title: 'workflow-management.title',
      },
      {
        path: RouteSegment.Preferences,
        loadChildren: () => import('./pages/preferences/preferences.routes').then((m) => m.PreferencesRoutes),
        title: 'preferences.title',
      },
      {
        path: RouteSegment.Settings,
        loadChildren: () => import('./pages/settings/settings.routes').then((m) => m.SettingsRoutes),
        canActivate: [
          createPermissionGuard([
            PermissionStoreModelEnum.ViewAnyAuditLogs,
            PermissionStoreModelEnum.ViewOwnAuditLogs,
            PermissionStoreModelEnum.ViewAnyUsers,
            PermissionStoreModelEnum.ViewOwnUsers,
          ]),
        ],
        title: 'side-menu.settings',
      },
    ],
  },
];
